import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  permissionTable,
  fieldTable,
  heading,
} from '../helpers';

const hkPerm = permissionTable([
  ['manage_housekeeping', 'Required for HK boards, cleanings, staff, maintenance, and lost & found'],
]);

export const housekeepingGuides = [
  buildGuide({
    id: 'housekeeping-overview',
    title: 'Housekeeping Overview',
    moduleId: 'housekeeping',
    summary:
      'Keep rooms sellable: status board, cleanings, staff, maintenance, and lost & found.',
    keywords: ['housekeeping', 'hk', 'cleaning', 'room status'],
    pathHint: 'Hotel → Housekeeping',
    related: [
      'housekeeping-room-status',
      'housekeeping-cleanings',
      'housekeeping-staff',
      'housekeeping-maintenance',
      'housekeeping-lost-found',
    ],
    overview: [
      ...paragraphs(
        'Housekeeping in Roomnexa keeps rooms safe to sell. Front office can only assign keys confidently when the Room Status board shows Clean / Vacant — not Dirty, Occupied, or Out of Order.',
        'The module is organised into tabs: Room Status (live board), Room Cleanings (work orders), Staff (attendants for assignment), Maintenance (repairs that block selling), and Lost & Found (guest items left behind).',
        'Example busy checkout morning: guests leave Rooms 101–108 → rooms show dirty → supervisors create or complete cleaning jobs → attendants finish floor-wise → status returns to Clean → front office assigns those rooms to afternoon arrivals and walk-ins.',
        'HK and front desk must share one truth. If FO assigns from memory while HK still shows Dirty, guests walk into unmade rooms. If HK marks Clean without inspection, VIP complaints follow.',
        'First-time staff should start every shift on Room Status, then work Cleanings, and escalate broken rooms through Maintenance immediately — do not leave OOO rooms unexplained on the board.'
      ),
      table(
        ['Tab', 'Purpose', 'Who uses it most'],
        [
          ['Room Status', 'Live clean/dirty/OOO board by floor', 'FO + HK supervisor'],
          ['Room Cleanings', 'Create and complete cleaning jobs', 'HK supervisor / attendants'],
          ['Staff', 'Housekeeping staff records', 'HK supervisor'],
          ['Maintenance', 'Repair tickets', 'HK + engineering'],
          ['Lost & Found', 'Guest item register', 'HK + front desk'],
        ]
      ),
    ],
    beforeYouBegin: [
      hkPerm,
      bullets([
        'Know today’s checkout list and same-day rebooked rooms from front office',
        'Agree priority: VIP arrivals and same-day resell rooms first',
        'Confirm who updates Roomnexa — attendants with login vs supervisor-only updates',
        'Have floor keys / access and linen stock ready before the cleaning wave',
      ]),
      callout(
        'important',
        'Permission',
        'If Housekeeping tabs are missing, ask admin for manage_housekeeping. Do not track status only on paper while FO uses Roomnexa.'
      ),
    ],
    steps: [
      heading('Daily HK rhythm'),
      steps([
        {
          text: 'Open Housekeeping at shift start and land on Room Status.',
        },
        {
          text: 'Walk the board by floor: note Dirty checkouts, stay-over service rooms, and OOO/maintenance.',
        },
        {
          text: 'Agree priority with front office — which dirty rooms are already sold again tonight?',
        },
        {
          text: 'Create or assign cleaning jobs under Room Cleanings for checkout and stay-over work.',
        },
        {
          text: 'As jobs complete and pass inspection, confirm Room Status shows Clean / ready to sell.',
        },
        {
          text: 'Log maintenance immediately when a room cannot be sold (AC dead, leak, lock fault).',
        },
        {
          text: 'Record Lost & Found items with room and date before the next guest arrives.',
        },
        {
          text: 'Do a midday board walk with FO after the peak checkout wave; repeat before evening arrivals.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Tab bar across Room Status, Cleanings, Staff, Maintenance, Lost & Found',
          'Floor/room cards or lists with status badges',
          'Action buttons to update status, create jobs, or open tickets',
        ],
        'Common screen elements'
      ),
      fieldTable([
        ['Room Status tab', '—', 'Live sellability board'],
        ['Room Cleanings tab', '—', 'Work orders for turnover'],
        ['Staff tab', '—', 'Attendant master for assignment'],
        ['Maintenance tab', '—', 'Repair tickets linked to rooms'],
        ['Lost & Found tab', '—', 'Guest property register'],
      ]),
    ],
    workflow: [
      workflow('Room turnover', [
        'Guest checkout',
        'Room marked dirty',
        'Cleaning job',
        'Inspection / clean',
        'Front office resells / assigns',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Room status drives what front office can safely assign at check-in. Out-of-order and maintenance states remove or warn against selling that room until cleared.',
        'Checkout from Booking typically frees the room into a dirty state for housekeeping. Completing cleanings is what returns the room to Clean for the next guest.',
        'Maintenance tickets and inventory blocks work together for longer closures: HK marks the operational problem; inventory/blocks stop overselling when needed.',
        'Lost & Found is an operational register — it does not automatically charge guests or alter booking status.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Same-day resell',
        'Prioritise cleanings for rooms already rebooked tonight. Ask FO for the “must clean first” list every morning.'
      ),
      callout(
        'tip',
        'One board',
        'Train both FO and HK to trust Room Status — not WhatsApp photos alone — as the assignable truth.'
      ),
      callout(
        'note',
        'Shift handover',
        'Leave open OOO reasons and unfinished VIP cleans in notes so the next supervisor does not guess.'
      ),
    ],
    bestPractices: [
      bullets([
        'HK and front desk share one truth — the Room Status board.',
        'Close maintenance tickets the same day when fixed, and tell FO the room is back.',
        'Inspect VIP rooms after clean before marking ready.',
        'Batch attendants floor-wise to cut travel time.',
        'Protect guest privacy when logging Lost & Found (secure storage, limited access).',
        'Reconcile dirty-room count with today’s checkouts after peak departure hour.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Marking clean without inspection.',
        'Leaving OOO rooms unexplained.',
        'FO assigning dirty rooms while HK is mid-clean.',
        'Delaying maintenance tickets until evening oversell risk.',
        'Vague Lost & Found descriptions that cannot identify the item later.',
        'Updating paper lists but not Roomnexa — FO sells the wrong room.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Status wrong on board',
            'Cleaning not completed / stale view',
            'Complete cleaning job; refresh Room Status',
          ],
          [
            'Tab missing',
            'Missing manage_housekeeping',
            'Ask admin to grant permission',
          ],
          [
            'FO says no clean rooms but HK disagrees',
            'Status not updated after clean',
            'Mark jobs complete; joint board walk',
          ],
          [
            'Room sold while broken',
            'OOO not logged',
            'Open Maintenance immediately; reallocate guest',
          ],
          [
            'Attendant not in assign list',
            'Staff record missing/inactive',
            'Add/activate under Staff tab',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Who updates status — FO or HK?',
          a: 'Usually HK supervisors; FO must read Room Status before assigning rooms. Some properties allow FO to view-only.',
        },
        {
          q: 'Do cleaners need Roomnexa logins?',
          a: 'Only if they personally update jobs. Many hotels have supervisors update the board while attendants work physically.',
        },
        {
          q: 'When should I open Maintenance vs just Dirty?',
          a: 'Dirty means needs cleaning. Maintenance/OOO means the room is unsafe or unusable even after cleaning.',
        },
        {
          q: 'How does this link to check-in?',
          a: 'Front office should assign only Clean vacant rooms. See Check-in and Room Allocation guides.',
        },
        {
          q: 'Where do checkout rooms appear?',
          a: 'After Booking checkout, rooms need cleaning — use Room Status and Room Cleanings to turn them around.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'housekeeping-room-status',
    title: 'Room Status Board',
    moduleId: 'housekeeping',
    summary: 'See every room’s cleanliness and occupancy state by floor.',
    keywords: [
      'room status',
      'clean',
      'dirty',
      'ooo',
      'vacant',
      'occupied',
    ],
    pathHint: 'Housekeeping → Room Status',
    related: [
      'housekeeping-cleanings',
      'check-in',
      'room-allocation',
    ],
    overview: [
      ...paragraphs(
        'Room Status is the live board used before assigning keys. It shows which rooms are clean, dirty, occupied, or out of order, often grouped by floor so supervisors can dispatch attendants efficiently.',
        'Front office should glance at this board (or an agreed shared view) before every check-in and walk-in assignment. A vacant room is not automatically sellable — it must also be Clean.',
        'Example: Arrival for Deluxe at 1 PM. Booking is reserved. Room Status shows Deluxe 402 Dirty (morning checkout) and 405 Clean Vacant. Allocate 405, or prioritise cleaning 402 if the guest specifically requested that number.',
        'Statuses change from cleaning completions, maintenance tickets, and booking check-in/out events. If the board looks “stuck,” complete pending cleaning jobs and refresh.',
        'OOO / maintenance rooms must stay visible and explained. Hidden broken rooms get sold by mistake during peak walk-in nights.'
      ),
    ],
    beforeYouBegin: [
      hkPerm,
      bullets([
        'Know floor layout and which rooms are VIP / accessible / connecting',
        'Have today’s arrivals and checkouts from front office',
        'Agree badge meanings with your property (Clean, Dirty, Occupied, OOO)',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Housekeeping → Room Status.' },
        {
          text: 'Filter or scan by floor. Start with floors that have the most checkouts.',
        },
        {
          text: 'Identify dirty checkout rooms that need priority cleaning — especially same-day resells.',
        },
        {
          text: 'Confirm Clean vacant rooms for upcoming arrivals and share the list with Room Allocation.',
        },
        {
          text: 'Flag Occupied rooms that still need stay-over service.',
        },
        {
          text: 'Coordinate with front office on every OOO: reason, ETA to return, whether inventory is blocked.',
        },
        {
          text: 'After each cleaning wave, recheck the board so FO is not working from memory.',
        },
        {
          text: 'Before evening peak, do a final Clean vacant count by room type for walk-ins.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Floor groups',
          'Room tiles/rows',
          'Status badges',
          'Occupancy indicators',
        ],
        'Board elements'
      ),
      table(
        ['Typical status', 'Front-office meaning', 'HK action'],
        [
          ['Clean / Vacant', 'Safe to assign', 'Hold ready; inspect VIPs'],
          ['Dirty', 'Do not assign until cleaned', 'Create/complete cleaning job'],
          ['Occupied', 'In-house', 'Stay-over clean per schedule'],
          ['OOO / Maintenance', 'Do not sell', 'Repair; close ticket; then clean'],
        ]
      ),
      fieldTable([
        ['Floor filter / groups', '—', 'Focus attendant dispatch'],
        ['Room badge', '—', 'Clean, dirty, occupied, OOO'],
        ['Room number', '—', 'Physical unit FO will assign'],
      ]),
    ],
    workflow: [
      workflow('Arrival prep', [
        'Open status board',
        'List clean rooms by type',
        'Allocate arrivals',
        'Recheck after HK wave',
        'Release keys at check-in',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Statuses change from cleanings, maintenance, and booking check-in/out events. The board is operational truth for assignability.',
        'Selling availability for OTAs is primarily inventory/booking driven; OOO should still be reflected so FO does not assign a broken room, and longer closures may need inventory blocks.',
        'A room can be vacant and dirty at once — vacancy alone never means “hand the keys.”',
        'Occupied rooms should not be reassigned to new arrivals until checkout and cleaning complete (except authorised room moves).'
      ),
    ],
    tips: [
      callout(
        'tip',
        'VIP rooms',
        'Keep a Clean room noted for VIP before general allocation so walk-ins do not take it first.'
      ),
      callout(
        'tip',
        'Two board walks',
        'Do a board walk with FO at 8–9 AM and again after peak checkout.'
      ),
      callout(
        'warning',
        'Never guess',
        'If a room looks Clean in person but Dirty in Roomnexa, update the system before FO assigns — or you will double-assign.'
      ),
    ],
    bestPractices: [
      bullets([
        'Do a board walk with FO at 8–9 AM and after peak checkout.',
        'Count Clean vacant rooms by type before promising walk-in discounts.',
        'Label OOO with cause and expected return time in maintenance notes.',
        'Prioritise floors that unblock the most afternoon arrivals.',
        'Keep accessible and connecting rooms visible in planning, not buried.',
        'Refresh before every rush — stale boards cause dirty-room check-ins.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Assigning dirty rooms to rush arrivals.',
        'Ignoring OOO badges during night walk-ins.',
        'Updating only one floor while FO sells another.',
        'Marking Clean while bathroom/linen still pending.',
        'Leaving occupied rooms unmarked after check-in.',
        'Using a private spreadsheet that disagrees with Room Status.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Board stale',
            'Jobs not completed / need refresh',
            'Refresh; complete pending cleaning jobs',
          ],
          [
            'All rooms look occupied',
            'Checkouts not finished in Booking',
            'Confirm FO completed checkout; then clean',
          ],
          [
            'Clean room not offered at check-in',
            'FO on wrong type / filter',
            'Share exact room numbers; verify type match',
          ],
          [
            'OOO still selling',
            'Inventory not blocked / FO override',
            'Open Maintenance; align with inventory blocks; brief FO',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Does status sync to OTAs?',
          a: 'Selling availability is inventory/booking driven; OOO should be reflected via inventory blocks or room state so FO does not sell the room.',
        },
        {
          q: 'Vacant vs Clean?',
          a: 'Vacant means no guest in-house. Clean means ready to sell. You need both for safe assignment.',
        },
        {
          q: 'Who marks Dirty after checkout?',
          a: 'Checkout flows and HK process update readiness; supervisors confirm Dirty rooms enter the cleaning queue.',
        },
        {
          q: 'Can FO edit status?',
          a: 'Depends on role permissions. Best practice is HK ownership with FO read-before-assign.',
        },
        {
          q: 'What if physical room is clean but board says Dirty?',
          a: 'Complete/close the cleaning job and refresh. Do not assign on verbal assurance alone.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'housekeeping-cleanings',
    title: 'Room Cleanings Workflow',
    moduleId: 'housekeeping',
    summary:
      'Create, assign, and complete cleaning jobs for checkout and stay-over rooms.',
    keywords: ['cleaning', 'cleanings', 'housekeeping job', 'turnover'],
    pathHint: 'Housekeeping → Room Cleanings',
    related: [
      'housekeeping-room-status',
      'housekeeping-staff',
      'check-out',
    ],
    overview: [
      ...paragraphs(
        'Room Cleanings tracks work orders for cleaning. Supervisors create jobs, assign staff when your process uses assignment, and complete jobs so room status moves toward clean/ready.',
        'There are different job intents in daily ops: checkout cleans (full turnover), stay-over / service cleans for occupied rooms, and deeper cleans when your property schedules them.',
        'Example after checkout: Guest leaves Room 216 at 11:05. FO completes Check-out. HK creates or picks up the cleaning job for 216, assigns attendant Meena, inspects after linen/bathroom, marks complete — Room Status shows Clean Vacant for the 3 PM arrival.',
        'Completing a job while the bathroom or bed is unfinished is the fastest way to get a guest complaint. Inspection is part of “complete,” especially for VIP and same-day resell rooms.',
        'Batch jobs by floor so attendants are not crossing the building for every room. Log skipped rooms with a reason (guest refused service, DND, no linen).'
      ),
    ],
    beforeYouBegin: [
      hkPerm,
      bullets([
        'Checkout list and stay-over service list for the day',
        'Staff records active under Housekeeping → Staff if you assign jobs',
        'Linen, amenities, and maintenance issues identified before starting',
        'Know VIP / early arrival priority from front office',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Housekeeping → Room Cleanings.' },
        {
          text: 'Review open jobs leftover from the previous shift; clear blockers first.',
        },
        {
          text: 'Create a cleaning job for each room that needs work (checkout, stay-over, or deep clean as your fields allow).',
        },
        {
          text: 'Assign staff if your process uses assignment — pick active attendants from Staff.',
        },
        {
          text: 'Dispatch floor-wise. Attendants clean; supervisors spot-check bathrooms, beds, and amenities.',
        },
        {
          text: 'When finished and inspected, mark the job complete per your fields.',
        },
        {
          text: 'Verify Room Status shows the room ready to sell (Clean).',
        },
        {
          text: 'Notify front office which priority rooms are released for allocation/check-in.',
        },
        {
          text: 'Log skipped or delayed rooms with reason so night shift knows what remains.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Cleaning list / queue',
          'Create job action',
          'Assignment fields',
          'Job status',
          'Complete / inspect action',
        ],
        'Cleanings screen areas'
      ),
      fieldTable([
        ['Room', 'Yes', 'Which physical room to clean'],
        ['Job type / notes', 'Recommended', 'Checkout vs stay-over vs special'],
        ['Assigned staff', 'If used', 'Attendant accountability'],
        ['Status', '—', 'Open / in progress / complete'],
        ['Complete action', 'Yes', 'Closes job and supports Clean status'],
      ]),
    ],
    workflow: [
      workflow('Checkout clean', [
        'Checkout completes',
        'Dirty room on board',
        'Create/assign cleaning',
        'Clean + inspect',
        'Mark complete',
        'Clean status',
        'FO resells / assigns',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Completing cleanings is what makes rooms assignable again after checkout. Leaving jobs open keeps Dirty badges in front of FO.',
        'Stay-over cleans do not free the room for a new guest; they service an occupied room on schedule.',
        'Assignment to Staff records is operational accountability — it is separate from whether the attendant has a Roomnexa login under Users/Roles.',
        'If status does not update after complete, refresh Room Status and re-open the job to confirm it saved.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Batch by floor',
        'Send attendants floor-wise to reduce travel time and finish arrival rooms faster.'
      ),
      callout(
        'tip',
        'Same-day resell first',
        'If 308 is sold again tonight and 309 is empty tonight, clean 308 first even if 309 checked out earlier.'
      ),
      callout(
        'note',
        'DND / refused service',
        'Do not mark Clean as a shortcut. Note “guest refused service” and keep the true status.'
      ),
    ],
    bestPractices: [
      bullets([
        'Inspect after clean before marking ready for VIP.',
        'Log skipped rooms with reason.',
        'Confirm mini-bar / Lost & Found check during checkout cleans.',
        'Escalate maintenance found mid-clean before marking the room sellable.',
        'Keep a running “released to FO” list during peak.',
        'Balance workload so one attendant is not stuck with all suites.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Closing job while bathroom/linen incomplete.',
        'Assigning jobs to inactive staff.',
        'Cleaning without checking Room Status priority.',
        'Forgetting stay-over rooms until evening complaints.',
        'Marking complete without verifying the board updated.',
        'Starting deep cleans on rooms needed for afternoon arrivals.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot complete',
            'Required fields/staff missing',
            'Ensure required fields/staff selected',
          ],
          [
            'Status not updating',
            'Job not saved / view stale',
            'Refresh Room Status; re-open job; complete again',
          ],
          [
            'Wrong room cleaned',
            'Mis-assigned job',
            'Verify room number on job vs door; correct board; inform FO',
          ],
          [
            'No staff to assign',
            'Staff list empty',
            'Add attendants under Staff tab',
          ],
          [
            'Too many open jobs',
            'Peak checkout without priority',
            'Re-order by FO same-day resell list',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Stay-over cleans?',
          a: 'Yes — create cleaning jobs for occupied rooms on service days without checking the guest out.',
        },
        {
          q: 'Must every checkout have a job?',
          a: 'Operationally yes — every dirty room needs a path back to Clean, whether created per room or via your property’s bulk process.',
        },
        {
          q: 'Who marks complete — attendant or supervisor?',
          a: 'Follow SOP. Many hotels require supervisor inspection before complete on VIP rooms.',
        },
        {
          q: 'Link to check-out?',
          a: 'Booking Check-out frees the guest stay; Room Cleanings turns the physical room around for the next sale.',
        },
        {
          q: 'What if the guest returns for a forgotten bag mid-clean?',
          a: 'Pause safely, involve FO/security per policy, log Lost & Found if needed, then resume cleaning.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'housekeeping-staff',
    title: 'Housekeeping Staff',
    moduleId: 'housekeeping',
    summary:
      'Maintain housekeeping staff records used for assignment and accountability.',
    keywords: ['hk staff', 'attendants', 'housekeeping employees'],
    pathHint: 'Housekeeping → Staff',
    related: ['housekeeping-cleanings', 'users-roles'],
    overview: [
      ...paragraphs(
        'The Staff tab stores housekeeping personnel you can associate with cleaning work. It is the operational roster for assignment — who cleaned Room 118 this morning — not the same thing as a Roomnexa login.',
        'Keep names and contacts current so supervisors can assign jobs quickly and call attendants during peak turnover. Inactive people who left the property should not remain selectable.',
        'Example: New attendant joins. Supervisor adds Staff record with name and mobile, briefs SOP, then assigns checkout cleans on floors 2–3. Completion history stays attributable.',
        'System access is separate: Users & Roles controls who can open Housekeeping in Roomnexa. A cleaner may appear in HK Staff without ever logging in; a supervisor may have both a Staff context and a user login.',
        'Update the list every payroll cycle or whenever contractors rotate — stale rosters cause jobs assigned to people who are not on property.'
      ),
    ],
    beforeYouBegin: [
      hkPerm,
      bullets([
        'Legal name and contact for each attendant',
        'Know who is active on today’s shift',
        'Understand whether attendants need Roomnexa logins (usually supervisors only)',
      ]),
      callout(
        'note',
        'System users vs HK staff',
        'A cleaner may appear in HK Staff without a Roomnexa login. Logins are managed under Users/Roles.'
      ),
    ],
    steps: [
      steps([
        { text: 'Open Housekeeping → Staff.' },
        {
          text: 'Review the list; deactivate people who left or are on long leave if your fields support it.',
        },
        {
          text: 'Add staff with name and contact details as required.',
        },
        {
          text: 'Edit records when numbers change — WhatsApp coordination depends on them.',
        },
        {
          text: 'Use only active staff when assigning cleanings.',
        },
        {
          text: 'Brief new attendants on how room numbers map to floors and priority VIP rooms.',
        },
        {
          text: 'If a supervisor needs system access, request a user account separately via Users/Roles — do not assume Staff add creates a login.',
        },
        {
          text: 'Spot-check assignment dropdowns on Room Cleanings after adding someone new.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Name', 'Yes', 'Attendant identity on jobs'],
        ['Contact', 'Recommended', 'Shift coordination'],
        ['Active flag', 'Depends', 'Availability for assignment'],
        ['Edit / deactivate', '—', 'Keep roster accurate'],
      ]),
    ],
    workflow: [
      workflow('Onboard attendant', [
        'Add staff record',
        'Brief SOP & floors',
        'Assign cleaning jobs',
        'Review completion quality',
        'Keep roster updated',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Staff records are operational HK data used for assignment and accountability on cleaning jobs.',
        'Login access is controlled separately under Users/Roles with permissions such as manage_housekeeping.',
        'Assigning jobs to inactive or missing staff breaks the cleaning workflow and leaves rooms Dirty on the board.',
        'Staff identity on a job helps dispute handling (“who cleaned this room?”) without exposing guest folio data to every attendant.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Shift codes in names',
        'If helpful, include shift hints in notes/name conventions your property agrees (e.g. floor responsibility) — keep it consistent.'
      ),
      callout(
        'tip',
        'One owner',
        'Nominate one supervisor to own roster accuracy so duplicates do not multiply.'
      ),
      callout(
        'note',
        'Privacy',
        'Staff contact numbers are internal. Do not publish them on guest-facing materials.'
      ),
    ],
    bestPractices: [
      bullets([
        'Keep the list current every payroll cycle.',
        'Deactivate leavers the same day their last shift ends.',
        'Match assignment names to how attendants are known on radio/WhatsApp.',
        'Train substitutes before festival peaks; add them to Staff early.',
        'Do not share one Staff identity across two people.',
        'Separate contractor agencies clearly in naming if multiple vendors work on site.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Assigning jobs to inactive staff.',
        'Assuming Staff add creates a Roomnexa login.',
        'Duplicate records for the same person with different spellings.',
        'Leaving outdated mobile numbers.',
        'Letting FO invent attendant names in notes instead of using Staff.',
        'Deleting history carelessly when someone leaves — prefer deactivate when available.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Staff not listed',
            'Never created or deactivated',
            'Create Staff record; ensure not deactivated',
          ],
          [
            'Cannot assign on cleanings',
            'Empty/inactive roster',
            'Add active staff; refresh cleanings screen',
          ],
          [
            'Wrong person credited',
            'Duplicate names',
            'Edit/merge per SOP; use unique naming',
          ],
          [
            'Attendant cannot open Housekeeping',
            'No user login / permission',
            'Create user under Users/Roles if they must use the app',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Do HK staff need app logins?',
          a: 'Only if they personally use Roomnexa. Many properties have supervisors update the board.',
        },
        {
          q: 'Is Staff the same as Hotel Users?',
          a: 'No. Staff is HK operational roster. Hotel users/roles control system login and permissions.',
        },
        {
          q: 'Can I assign one job to two people?',
          a: 'Follow what the form allows. Operationally, name a primary attendant and note helpers in job notes if needed.',
        },
        {
          q: 'What about agency housekeepers?',
          a: 'Add them as Staff for the days they work; deactivate when the contract day ends.',
        },
        {
          q: 'Where do I see who cleaned a room?',
          a: 'On the cleaning job assignment / history for that room’s job in Room Cleanings.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'housekeeping-maintenance',
    title: 'Maintenance Requests',
    moduleId: 'housekeeping',
    summary:
      'Log and close room/facility maintenance so damaged rooms are not sold.',
    keywords: [
      'maintenance',
      'repair',
      'ooo',
      'out of order',
      'fix',
    ],
    pathHint: 'Housekeeping → Maintenance',
    related: ['housekeeping-room-status', 'inventory-blocks'],
    overview: [
      ...paragraphs(
        'Maintenance tracks repair needs (AC, plumbing, electrical, furniture, locks). Open tickets when a room is unsafe or unusable; close them when fixed so front office can sell again.',
        'A dirty room needs cleaning. A room with a dead AC in summer needs Maintenance (and usually OOO on the status board) even if the bed is made. Selling a broken room creates refunds and reviews that cost more than one night’s rent.',
        'Example: Guest reports water leak in 512. Move the guest first if occupied, open Maintenance with room + description + priority, mark unsellable on Room Status, repair, close ticket, clean, then tell FO the room is back. For multi-day closures, align with Inventory Blocks.',
        'Open tickets should be reviewed daily. “We’ll fix it next week” rooms left unmarked are how night desks accidentally walk guests into them.',
        'Maintenance is repairs. Lost & Found is guest property. Do not mix the two registers.'
      ),
    ],
    beforeYouBegin: [
      hkPerm,
      bullets([
        'Know room number and clear issue description',
        'If occupied, plan guest move with front office first when unsafe',
        'Know whether a longer closure needs an inventory block',
        'Photos/notes if your process stores evidence',
      ]),
      callout(
        'warning',
        'Guest safety',
        'If a room is unsafe, move the guest first, then log maintenance.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Confirm the issue physically (or from a credible guest/attendant report).',
        },
        {
          text: 'If the room is occupied and unsafe/unusable, coordinate a room move with FO before deep troubleshooting.',
        },
        { text: 'Open Housekeeping → Maintenance.' },
        {
          text: 'Create a request with room, issue description, and priority.',
        },
        {
          text: 'Ensure Room Status shows the room as unsellable / OOO per your process.',
        },
        {
          text: 'For multi-day closures, ask the inventory owner to place an Inventory Block so channels/FO do not oversell.',
        },
        {
          text: 'Coordinate physical repair with engineering/vendor.',
        },
        {
          text: 'Mark fixed/closed when resolved. Do not close early while waiting for parts unless policy allows a deferred reopen.',
        },
        {
          text: 'Clean the room, confirm Room Status is sellable, and tell front office it is back.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Open tickets list',
          'Create request form',
          'Status open/fixed',
          'Room reference',
          'Priority / description fields',
        ],
        'Maintenance screen areas'
      ),
      fieldTable([
        ['Room', 'Yes', 'Where the defect is'],
        ['Description', 'Yes', 'What is broken'],
        ['Priority', 'Recommended', 'Dispatch order'],
        ['Status', '—', 'Open vs fixed/closed'],
        ['Notes', 'Recommended', 'Vendor ETA, parts, safety'],
      ]),
    ],
    workflow: [
      workflow('OOO handling', [
        'Issue found',
        'Move guest if needed',
        'Open maintenance',
        'Mark room unsellable',
        'Inventory block if multi-day',
        'Repair',
        'Close ticket',
        'Clean & return to inventory',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Open maintenance often correlates with rooms that must not be assigned. Pair with inventory blocks for longer closures so availability and FO assignment stay aligned.',
        'Closing a ticket without telling FO leaves rooms idle. Closing before the fix is real puts guests back into broken rooms.',
        'Maintenance does not replace cleaning: after repair, the room usually still needs a cleaning job before Clean Vacant.',
        'Reporting and night audit should see why a room was offline — keep descriptions specific (“AC not cooling” not just “problem”).'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Photo evidence',
        'Photo evidence in notes (if your process allows) speeds vendor decisions and dispute handling.'
      ),
      callout(
        'tip',
        'Daily open-ticket review',
        'Start the HK briefing with open maintenance count — treat it like dirty-room count.'
      ),
      callout(
        'warning',
        'Do not soft-sell OOO',
        'Never assign an OOO room as a “temporary” walk-in solution.'
      ),
    ],
    bestPractices: [
      bullets([
        'Photo evidence in notes if your process allows.',
        'Review open tickets daily.',
        'Write clear ETAs for FO (“back after 6 PM” vs “sometime”).',
        'Link longer OOOs to Inventory Blocks.',
        'After close, verify Room Status and run a cleaning job.',
        'Escalate safety issues (electrical, gas, structural) immediately to duty manager.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Closing tickets before parts arrive.',
        'Forgetting to tell FO the room is back.',
        'Logging maintenance but leaving the room looking Clean Vacant.',
        'Vague descriptions that engineering cannot action.',
        'Using Dirty instead of OOO for broken rooms.',
        'Blocking inventory forever after the room was fixed.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Room still assigned',
            'FO sold before OOO logged',
            'Reallocate guest; update status board; open ticket',
          ],
          [
            'Room offline on channels too long',
            'Inventory block not lifted',
            'Close maintenance; remove/adjust inventory block',
          ],
          [
            'Ticket cannot close',
            'Required fields / permission',
            'Complete fields; ask user with manage_housekeeping',
          ],
          [
            'Repeat issues',
            'Root cause not fixed',
            'Keep ticket history; escalate vendor; consider longer OOO',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Maintenance vs Lost & Found?',
          a: 'Maintenance is repairs. Lost & Found is guest property.',
        },
        {
          q: 'When do I use Inventory Blocks?',
          a: 'For multi-day or sellable-inventory closures beyond a quick same-day repair — see inventory-blocks.',
        },
        {
          q: 'Can I open maintenance for public areas?',
          a: 'Use room/facility fields as your deployment allows; still keep FO informed if sellable rooms are affected.',
        },
        {
          q: 'Who prioritises tickets?',
          a: 'HK supervisor with duty manager — safety and in-house guest comfort first, then arrival impact.',
        },
        {
          q: 'Does closing maintenance auto-clean the room?',
          a: 'No. After repair, complete a cleaning job and confirm Clean on Room Status.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'housekeeping-lost-found',
    title: 'Lost & Found',
    moduleId: 'housekeeping',
    summary:
      'Register items left by guests with room, date, and return status.',
    keywords: [
      'lost and found',
      'lost & found',
      'left behind',
      'guest item',
    ],
    pathHint: 'Housekeeping → Lost & Found',
    related: ['housekeeping-overview', 'customer-detail'],
    overview: [
      ...paragraphs(
        'Lost & Found prevents disputes by logging what was found, where, when, and whether it was returned or disposed per property policy. Guests often call days later — the register is your proof.',
        'Example: Attendant finds a black Samsung phone charger and a blue passport in Room 207 after checkout. Log two entries (or one with clear multi-item detail per SOP), store securely, tell FO, and contact the guest using booking/customer details. Passports and jewellery escalate to the duty manager immediately.',
        'Good descriptions matter: “Apple MagSafe white cable, 1m, found under bed, Room 207, 20 Jul, bag L&F-042” beats “charger.” Vague entries cannot be matched when three guests call.',
        'This register does not automatically charge guests or change booking status. Shipping costs, COD, or disposal timelines follow your property policy — note what you promised.',
        'Front desk may help identify the guest via Booking List or Customer Detail, but Lost & Found itself remains a staff-only register — guests do not browse it.'
      ),
    ],
    beforeYouBegin: [
      hkPerm,
      bullets([
        'Item in hand with secure storage location ready',
        'Room number and date/time found',
        'Sealed bag / tag number if your SOP uses them',
        'Know high-value escalation path (manager / security)',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Secure the item immediately — do not leave it in the open room for the next guest.',
        },
        { text: 'Open Housekeeping → Lost & Found.' },
        {
          text: 'Add item description (brand, colour, quantity), room number, date found, and storage location / tag number.',
        },
        {
          text: 'Link guest/booking in notes if known (name, mobile, booking code). Use Customer Detail / Booking List to identify when needed.',
        },
        {
          text: 'Escalate passports, cash, jewellery, and electronics to duty manager per policy.',
        },
        {
          text: 'FO contacts the guest using official channels; note contact attempts on the entry.',
        },
        {
          text: 'When returned, update status and who collected it (ID checked).',
        },
        {
          text: 'For unclaimed items, follow retention/disposal policy and record the outcome — do not silently discard.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Register list',
          'Add item form',
          'Status fields (found / returned / disposed)',
          'Search by room/date',
        ],
        'Lost & Found areas'
      ),
      fieldTable([
        ['Item description', 'Yes', 'What was found — be specific'],
        ['Room number', 'Yes', 'Where it was found'],
        ['Date found', 'Yes', 'Timeline for guest claims'],
        ['Storage location', 'Recommended', 'Where staff can retrieve it'],
        ['Guest / booking notes', 'Recommended', 'Who to contact'],
        ['Return status', 'Yes at close', 'Returned / pending / disposed'],
      ]),
    ],
    workflow: [
      workflow('Item recovery', [
        'Found in room',
        'Secure & log entry',
        'Store with tag',
        'Identify guest',
        'Contact guest',
        'Return & close',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'This is an operational register — it does not automatically charge guests or alter folios.',
        'Accurate room/date linkage lets FO match callers to items without guessing across the whole hotel.',
        'High-value items may have separate custody rules (manager safe). The Roomnexa entry still records existence and outcome.',
        'Customer Detail helps identify repeat guests for contact, but Lost & Found visibility remains staff-only.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'High-value items',
        'Escalate passports, jewellery, cash, and laptops to the duty manager immediately — then log the entry.'
      ),
      callout(
        'tip',
        'One item, one clear line',
        'If multiple unrelated items are found, prefer separate entries so return status stays accurate.'
      ),
      callout(
        'note',
        'Do not message random numbers',
        'Use booking/customer contacts. Avoid posting item photos in public staff groups with guest-identifying data.'
      ),
    ],
    bestPractices: [
      bullets([
        'Use sealed bags with log numbers.',
        'Follow retention policy for unclaimed items.',
        'Photograph items when policy allows, stored securely.',
        'Train attendants to check drawers, safes, and bathrooms on checkout cleans.',
        'Update status the same day an item is returned.',
        'Reconcile physical shelf vs open register weekly.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Vague descriptions (“charger”) without brand/colour/room.',
        'Leaving items in the room for the next guest to find.',
        'Returning items without checking collector ID.',
        'Logging after disposing — no audit trail.',
        'Mixing maintenance junk with guest Lost & Found.',
        'Promising courier shipping without noting who pays.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot find entry',
            'Wrong date/room search',
            'Search by room/date; broaden date range; try guest name in notes',
          ],
          [
            'Guest claims item not logged',
            'Attendant skipped register',
            'Check physical L&F shelf; interview shift; tighten checkout-clean checklist',
          ],
          [
            'Two guests claim same item',
            'Vague description',
            'Use distinctive details + booking dates; manager decision; document',
          ],
          [
            'Storage location empty',
            'Moved without status update',
            'Recount shelf; update register; restrict access',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can guests see Lost & Found?',
          a: 'No — staff only. Share updates via call/WhatsApp per policy.',
        },
        {
          q: 'Should I create a customer profile?',
          a: 'Not required to log an item. Use Customer Detail / booking search when you need contact history.',
        },
        {
          q: 'What if the room number is unknown?',
          a: 'Log with location found (lobby, restaurant) and date; still store securely and note finder name.',
        },
        {
          q: 'Do we charge storage?',
          a: 'Only per property policy — note any fees separately; Lost & Found does not auto-bill.',
        },
        {
          q: 'How long do we keep items?',
          a: 'Follow your written retention policy and record disposal when the period ends.',
        },
      ]),
    ],
  }),
];
